/** Body tab — the raw request body (fetched from S3 when offloaded).
 *
 * All rendering (format detection, pretty-print, wrap, copy, download) lives
 * in the shared `DocumentViewer`; this tab only fetches. Inbound webhook
 * payloads are almost always minified, so the viewer pretty-prints by default.
 */
import { Alert, Box, CircularProgress } from '@mui/material';
import { useQuery } from '@tanstack/react-query';

import * as apiRequestsApi from '@/api/apiRequests';
import { DocumentViewer } from '@/components/DocumentViewer';

export function BodyTab({ requestId }: { requestId: number }) {
  const query = useQuery({
    queryKey: ['api-requests', 'body', requestId],
    queryFn: () => apiRequestsApi.body(requestId),
    staleTime: Infinity, // the stored body never changes
  });

  if (query.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (query.error || !query.data) {
    return <Alert severity="error">Couldn’t load the request body.</Alert>;
  }

  return (
    <DocumentViewer
      text={query.data.body ?? ''}
      formatHint={query.data.format}
      downloadName={`api-request-${requestId}-body`}
      copyMessage="Body copied to clipboard."
      emptyMessage="No body is stored for this request (expired or empty)."
    />
  );
}
